import { FC, useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import Layout from '@/components/layout/Layout';
import BookBreadcrumbs from '@/components/ui/book-breadcrumbs/BookBreadcrumbs';
import BookInfo from '@/components/ui/book-info/BookInfo';
import TabMenu from '@/components/ui/tab-menu/TabMenu';
import SimilarBooks from '@/components/ui/similar-books/SimilarBooks';
import PaginationBlock from '@/components/ui/paginationBlock/PaginationBlock';
import ReadBanner from '@/components/ui/banners/read-banner/ReadBanner';
import { getAuthorFullName } from '@/helpers/helpers';
import styles from './ReadDetail.module.scss';
import { IBookShortDetail } from '@/interfaces/bookShortDetail.interface';
import { IReviews } from '@/interfaces/reviews.interface';
import { IRead } from '@/interfaces/read.interface';
import { BTN_READ_ONLINE_LFROM } from '@/helpers/variables';
import ReadJsonMarkup from './read-json-markup/ReadJsonMarkup';

interface ReviewsDetailPage {
  book: IBookShortDetail;
  read: IRead;
  reviews: IReviews;
  pageNumber?: string;
}

const ReadDetail: FC<ReviewsDetailPage> = ({ book, read, reviews, pageNumber }) => {
  const bookData = book.book;
  const otherAuthorBooks = book.other_author_books;
  const { first_name, middle_name, last_name } = book.author;

  const authorFullName = getAuthorFullName(first_name, middle_name, last_name);

  const currentPage = read.page;
  const nextPage = currentPage + 1;
  const previousPage = currentPage - 1;
  const pagesCount = read.pages;

  const refReadMenu = useRef<HTMLDivElement>(null);
  const refReadContent = useRef<HTMLDivElement>(null);
  const refScrollContainer = useRef<HTMLDivElement>(null);

  const [isReadMenuActive, setIsReadMenuActive] = useState(false);

  const SEO_TITLE = `Книга ${bookData.title} - читать онлайн бесплатно, автор ${authorFullName}${currentPage > 1 ? `, страница ${currentPage}` : ``}`;
  const SEO_DESCRIPTION = `${bookData.title} «${authorFullName}» – читать онлайн бесплатно в электронной библиотеке фэнтези Fenzin.org.${currentPage > 1 ? ` Cтраница ${currentPage}` : ``}`;
  const SEO_CANONICAL = `/online/${book.id}`;
  const SEO_COVER = `/covers/${bookData.external_id}.jpg`;

  const createMarkup = (c: string) => {
    return { __html: c };
  };

  let renderBannersOnce = true;

  useEffect(() => {
    /* Scroll to read content*/
    if (!!refScrollContainer) {
      refScrollContainer.current?.scrollIntoView();
    }

    // Баннер yandex
    if (renderBannersOnce) {
      const paragraphs = refReadContent.current?.querySelectorAll('p');

      if (paragraphs !== undefined && paragraphs?.length > 0) {
        const middleParagraph = paragraphs[Math.ceil(paragraphs.length / 2)];

        const renderMiddleBanner = (idName: string, className: string) => {
          const yandexBanner = document.createElement('div');
          yandexBanner.setAttribute('id', idName);
          yandexBanner.setAttribute('data', className);

          middleParagraph.after(yandexBanner);
        };
        renderMiddleBanner('yandex_rtb_R-A-745629-7', 'read-banner-desktop');
        renderMiddleBanner('yandex_rtb_R-A-745629-8', 'read-banner-mobile');
      }
    }
    renderBannersOnce = false;

    //Меню чтения открыть/закрыть
    const handleClickOutside = (event: Event) => {
      const menuBtn = refReadMenu.current!.previousElementSibling;

      if ((refReadMenu.current && refReadMenu.current.contains(event.target as HTMLElement)) || event.target === menuBtn) {
        return;
      }
      setIsReadMenuActive(false);
    };
    document.addEventListener('click', handleClickOutside);

    //Меню чтения настройки текста
    const FONT_SIZE = 16;
    const LINE_HEIGHT = 1.2;
    const CONTAINER_WIDTH = 760;
    const FONT_SIZE_RANGE = 1;
    const FONT_HEIGHT_RANGE = 1;
    const FONT_WIDTH_RANGE = 3;
    const BOOK_SETTINGS_KEY_NAME = 'book-settings-key';

    const textContainer = document.querySelector<HTMLDivElement>('[data-book-reader-container]');
    const fontSizeRange = document.querySelector<HTMLInputElement>('[data-font-size-setting]');
    const fontHeightRange = document.querySelector<HTMLInputElement>('[data-font-height-setting]');
    const fontWidthRange = document.querySelector<HTMLInputElement>('[data-font-width-setting]');

    const bookData = {
      fontSize: FONT_SIZE,
      lineHeight: LINE_HEIGHT,
      containerWidth: CONTAINER_WIDTH,
      fontSizeRange: FONT_SIZE_RANGE,
      fontHeightRange: FONT_HEIGHT_RANGE,
      fontWidthRange: FONT_WIDTH_RANGE,
    };

    /*Save to Local Storage*/
    const saveUserData = (userData: object) => {
      localStorage.setItem(BOOK_SETTINGS_KEY_NAME, JSON.stringify(userData));
    };

    const changeFontSize = () => {
      const rangeValue = Number((fontSizeRange as HTMLInputElement).value);
      const sizeValue = FONT_SIZE + rangeValue;

      textContainer!.style.fontSize = `${sizeValue}px`;

      bookData.fontSize = sizeValue;
      bookData.fontSizeRange = rangeValue;

      saveUserData(bookData);
    };

    const changeFontHeight = () => {
      const rangeValue = Number((fontHeightRange as HTMLInputElement).value);
      const lineHeightValue = LINE_HEIGHT + rangeValue / 5;

      textContainer!.style.lineHeight = String(lineHeightValue);

      bookData.lineHeight = lineHeightValue;
      bookData.fontHeightRange = rangeValue;

      saveUserData(bookData);
    };

    const changeFontWidth = () => {
      const rangeValue = Number((fontWidthRange as HTMLInputElement).value);
      const containerWidthValue = CONTAINER_WIDTH + rangeValue * 38;

      textContainer!.style.maxWidth = `${containerWidthValue}px`;

      bookData.containerWidth = containerWidthValue;
      bookData.fontWidthRange = rangeValue;

      saveUserData(bookData);
    };

    /*Events*/
    fontSizeRange!.addEventListener('input', changeFontSize);
    fontHeightRange!.addEventListener('input', changeFontHeight);
    fontWidthRange!.addEventListener('input', changeFontWidth);

    /*Get from Local Storage*/
    let savedBookItems;

    if (localStorage.getItem(BOOK_SETTINGS_KEY_NAME) != null) {
      savedBookItems = JSON.parse(localStorage.getItem(BOOK_SETTINGS_KEY_NAME) || '');

      textContainer!.style.fontSize = `${savedBookItems.fontSize}px`;
      textContainer!.style.lineHeight = savedBookItems.lineHeight;
      textContainer!.style.maxWidth = `${savedBookItems.containerWidth}px`;

      fontSizeRange!.value = savedBookItems.fontSizeRange;
      fontHeightRange!.value = savedBookItems.fontHeightRange;
      fontWidthRange!.value = savedBookItems.fontWidthRange;
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  const handleToggleActiveClass = () => {
    setIsReadMenuActive(!isReadMenuActive);
  };

  return (
    <Layout title={SEO_TITLE} description={SEO_DESCRIPTION} canonicalHref={SEO_CANONICAL} cover={SEO_COVER}>
      <ReadJsonMarkup book={book} authorName={authorFullName} />
      <BookBreadcrumbs bookTitle={book.book.title} bookId={book.id} authorFullName={authorFullName} authorId={book.author.id} genres={book.genres} isExtraPage={true} extraPageName="Читать онлайн" />

      <BookInfo book={book} reviewsCount={reviews.total_count} readPage />

      <TabMenu bookId={book.id} bookType={book.type} reviewsCount={reviews.total_count} readTxt={true} />
      <div ref={refScrollContainer} id="readBlock"></div>

      <div data-read-settings-wrap className={styles.readSettings}>
        <div data-read-settings-button className={isReadMenuActive ? `${styles.readSettings__btn} ${styles.readSettings__btn_active}` : `${styles.readSettings__btn}`} onClick={handleToggleActiveClass}>
          Настройки чтения
        </div>

        <div data-read-settings className={styles.readSettings__block} ref={refReadMenu}>
          <div className={styles.inputRangeBlock}>
            <div className={styles.inputRangeBlock__txt}>Размер шрифта</div>
            <div className={styles.inputRangeBlock__control}>
              <input data-font-size-setting type="range" className={`${styles.inputRange} ${styles.inputRange_chrome}`} min="0" max="10" step="1" placeholder="0" />
            </div>
          </div>
          <div className={styles.inputRangeBlock}>
            <div className={styles.inputRangeBlock__txt}>Высота строк</div>
            <div className={styles.inputRangeBlock__control}>
              <input data-font-height-setting type="range" className={`${styles.inputRange} ${styles.inputRange_chrome}`} min="0" max="10" step="1" placeholder="0" />
            </div>
          </div>
          <div className={`${styles.inputRangeBlock} ${styles.inputRangeBlock_mobNone}`}>
            <div className={styles.inputRangeBlock__txt}>Поля</div>
            <div className={styles.inputRangeBlock__control}>
              <input data-font-width-setting type="range" className={`${styles.inputRange} ${styles.inputRange_chrome}`} min="0" max="15" step="1" placeholder="3" />
            </div>
          </div>
        </div>
      </div>

      <PaginationBlock bookId={book.id} pagesCount={pagesCount} currentPage={currentPage} />

      <div data-book-reader-container className={pageNumber === undefined || Number(pageNumber) === 1 ? `${styles.readBookContainer} ${styles.readBookContainer_firstPage}` : `${styles.readBookContainer}`}>
        {pagesCount > 1 && (
          <div className={styles.paginationBlockArrows}>
            {currentPage > 1 && <Link prefetch={false} className={styles.paginationBlockArrows__prev} href={`/online/${book.id}/${previousPage}`}></Link>}
            {currentPage < pagesCount && <Link prefetch={false} className={styles.paginationBlockArrows__next} href={`/online/${book.id}/${nextPage}`}></Link>}
          </div>
        )}
        <div data-book-reader ref={refReadContent} className={`${styles.book_readers} ${styles.read1} ${styles.readBookContainer__txt}`} dangerouslySetInnerHTML={createMarkup(read.content)}></div>
        <ReadBanner />
      </div>

      {currentPage === pagesCount && book.book.allow_full_free === 0 && (
        <div className={styles.readBookContainer__end}>
          <div className={styles.readBookContainer__title}>Конец ознакомительного фрагмента</div>
          {!book.hide_litres_data && (
            <Link prefetch={false} href={`https://www.litres.ru/pages/biblio_book/?art=${book.book.external_id}&lfrom=${BTN_READ_ONLINE_LFROM}`} className={styles.readBookContainer__btn} target="_blank">
              Купить и скачать всю книгу
            </Link>
          )}
        </div>
      )}

      <PaginationBlock bookId={book.id} pagesCount={pagesCount} currentPage={currentPage} extraClassName="paginationBlock_bottom" />

      {otherAuthorBooks.length > 0 && <SimilarBooks books={otherAuthorBooks} authorName={authorFullName} authorId={book.author.id} />}
    </Layout>
  );
};

export default ReadDetail;
